import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Empty, message, Modal, Table } from 'antd';
import omsRecycleOrderDraftApi from 'apis/oms_recycle_order_draft';
import { findLabelByValue } from 'commons/options';
import i18n from 'i18n';
import { groupBy } from 'lodash-es';
import { useMemo } from 'react';
import { useToggle } from 'react-use';
import { recycleConsignmentStoreList } from '../common';
import LOCALS from 'commons/locals';
import formatTime from 'utils/formatTime';

const DraftList = ({
  storeIdList,
  onUseDraft,
}: {
  storeIdList: number[];
  onUseDraft: (draftData: Record<string, any>) => void;
}) => {
  const { data: draftList, refetch } = useQuery({
    queryKey: ['draftList', storeIdList.length ? storeIdList : undefined],
    queryFn: async () => {
      if (storeIdList.length === 0) {
        return omsRecycleOrderDraftApi.getList(undefined);
      }

      const uniqueStoreIdList = Array.from(new Set(storeIdList));
      const draftListByStore = await Promise.all(
        uniqueStoreIdList.map((id) => omsRecycleOrderDraftApi.getList(id)),
      );
      return draftListByStore.flat();
    },
  });

  const groupedDraftList = useMemo(() => {
    return groupBy(draftList, 'storeId');
  }, [draftList]);

  const [draftListOpen, toggleDraftListOpen] = useToggle(false);
  const [deleteLoading, toggleDeleteLoading] = useToggle(false);

  return (
    <div>
      <Modal
        open={draftListOpen}
        onCancel={toggleDraftListOpen}
        footer={null}
        title={i18n.t('SXpNgLWetM')}
        width={800}
      >
        {draftList && draftList.length === 0 ? (
          <div>
            <Empty description={i18n.t('No data')} />
          </div>
        ) : (
          Object.entries(groupedDraftList).map(([storeId, innerDraftList]) => {
            return (
              <div key={storeId} className="mb-8">
                <h3>
                  {findLabelByValue(
                    Number(storeId),
                    recycleConsignmentStoreList,
                  )}
                </h3>

                <Table
                  dataSource={innerDraftList}
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 80 },
                    {
                      title: i18n.t(LOCALS.title),
                      dataIndex: 'title',
                      width: 300,
                    },
                    {
                      title: i18n.t(LOCALS.created_time),
                      dataIndex: 'createdAt',
                      render: (value) => formatTime(value),
                    },
                    {
                      title: i18n.t(LOCALS.option),
                      dataIndex: 'action',
                      render: (_, record) => {
                        return (
                          <div className="flex gap-4">
                            <Button
                              type="primary"
                              onClick={() => {
                                onUseDraft(record.data);
                              }}
                            >
                              {i18n.t('ThAdOdLIza')}
                            </Button>
                            <Button
                              danger
                              loading={deleteLoading}
                              onClick={async () => {
                                toggleDeleteLoading();
                                await omsRecycleOrderDraftApi.delete(record.id);
                                toggleDeleteLoading();
                                refetch();
                                message.success(
                                  i18n.t(LOCALS.successful_operation),
                                );
                              }}
                            >
                              {i18n.t('delete')}
                            </Button>
                          </div>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  size="small"
                  bordered
                ></Table>
              </div>
            );
          })
        )}
      </Modal>
      <Badge count={draftList?.length} size="default">
        <Button onClick={toggleDraftListOpen}>{i18n.t('SXpNgLWetM')}</Button>
      </Badge>
    </div>
  );
};

export default DraftList;
